// src/components/chatbot.js
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Avatar,
  TextField,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  CircularProgress,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import SendIcon from "@mui/icons-material/Send";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweepRounded";
import NoteAddIcon from "@mui/icons-material/NoteAddRounded";
import DownloadIcon from "@mui/icons-material/DownloadRounded";

import AgentBlaynaLogo from "../assets/Agent-Blayna-Logo.png";
import { apiFetch } from "../api/http"; // ✅ sends JWT automatically

const CHAT_PATH = "/api/chat";
const CLIENTS_PATH = "/api/clients/";
const DEALS_PATH = "/api/deals/";
const LISTINGS_PATH = "/api/listings/";

const STORAGE_KEY = "agentblayna_crm_chat_v1";

const seedAssistant = () => ({
  id: crypto.randomUUID(),
  role: "assistant",
  content:
    "Hi, I’m AgentBlayna, your smart assistant. Tell me about your clients, deals, or listings and I’ll help you decide what to do next.",
  timestamp: new Date().toISOString(),
});

// Simple contextual suggestions based on the last assistant response
const getContextualSuggestions = (lastAssistantText) => {
  if (!lastAssistantText) {
    return [
      "Give me a quick summary of my active deals",
      "Who should I follow up with today?",
      "Turn my current pipeline into a short action plan",
    ];
  }

  const text = lastAssistantText.toLowerCase();
  const suggestions = [];

  if (text.includes("deal") || text.includes("pipeline")) {
    suggestions.push("Which deals are at risk and why?");
    suggestions.push("Draft follow-up ideas for my top three deals");
  }
  if (text.includes("client") || text.includes("contact")) {
    suggestions.push("Suggest three touchpoints for my key clients");
    suggestions.push("Help me write a short check-in message");
  }
  if (text.includes("listing")) {
    suggestions.push("What should I do with my slow listings?");
    suggestions.push("Help me prep for a seller update call");
  }

  suggestions.push("Summarize this into a short, clear plan");
  suggestions.push("What should I do in the next 24 hours?");

  return [...new Set(suggestions)];
};

// Strip simple markdown bullets so you don't see leading * or - in replies
const cleanReply = (text) => {
  if (!text || typeof text !== "string") return text;

  const lines = text.split(/\r?\n/).map((line) => {
    const trimmed = line.replace(/^\s*[*-]\s+/, "");
    return trimmed;
  });

  return lines.join("\n");
};

export default function ChatPanel() {
  const theme = useTheme();
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [seedAssistant()];
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const [crmSnapshot, setCrmSnapshot] = useState(null);
  const [crmLoading, setCrmLoading] = useState(true);
  const [crmError, setCrmError] = useState(null);

  const scrollRef = useRef(null);

  // Persist chat
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Prefill from elsewhere in the app (e.g., "Discuss in chat")
  useEffect(() => {
    const prefill = localStorage.getItem("agentblayna_chat_prefill");
    if (prefill) {
      setInput(prefill);
      localStorage.removeItem("agentblayna_chat_prefill");
    }
  }, []);

  // Fetch CRM snapshot once on mount (clients + deals + listings)
  useEffect(() => {
    const fetchCrm = async () => {
      try {
        setCrmLoading(true);
        setCrmError(null);

        const [clientsRes, dealsRes, listingsRes] = await Promise.allSettled([
          apiFetch(CLIENTS_PATH),
          apiFetch(DEALS_PATH),
          apiFetch(LISTINGS_PATH),
        ]);

        const snapshot = {};

        if (clientsRes.status === "fulfilled" && clientsRes.value.ok) {
          snapshot.clients = await clientsRes.value.json();
        }
        if (dealsRes.status === "fulfilled" && dealsRes.value.ok) {
          snapshot.deals = await dealsRes.value.json();
        }
        if (listingsRes.status === "fulfilled" && listingsRes.value.ok) {
          snapshot.listings = await listingsRes.value.json();
        }

        if (!snapshot.clients && !snapshot.deals && !snapshot.listings) {
          setCrmError(
            "I couldn't load your CRM data. Log in again if needed — I’ll still give general advice."
          );
          setCrmSnapshot(null);
        } else {
          setCrmSnapshot(snapshot);
        }
      } catch (err) {
        console.error("Failed to load CRM data:", err);
        setCrmError(
          "I couldn't load your CRM data. Log in again if needed — I’ll still give general advice."
        );
        setCrmSnapshot(null);
      } finally {
        setCrmLoading(false);
      }
    };

    fetchCrm();
  }, []);

  const formatTime = (iso) =>
    new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const lastUser = [...messages].reverse().find((m) => m.role === "user");
  const lastAssistant = [...messages].reverse().find(
    (m) => m.role === "assistant"
  );
  const nextActionSuggestions = getContextualSuggestions(
    lastAssistant?.content
  );

  const buildSystemMessage = () => {
    if (!crmSnapshot) {
      return {
        role: "system",
        content:
          "You are AgentBlayna, an AI CRM assistant. You are helping a busy agent or team lead decide what to do next. " +
          "Speak like a real person, in short, clear paragraphs. Do not use bullet points, asterisks, numbered lists, tables, emojis, or decorative symbols. " +
          "Focus on practical ideas: who to call, what to say, and how to move deals, listings, and relationships forward.",
      };
    }

    const snapshotText = JSON.stringify(crmSnapshot).slice(0, 8000);

    return {
      role: "system",
      content:
        "You are AgentBlayna, an AI CRM assistant inside a real estate CRM. " +
        "You can see a JSON snapshot that may include clients, deals, and listings.\n\n" +
        "The JSON might look like:\n" +
        snapshotText +
        "\n\nGuidelines:\n" +
        "- Use this data when you talk about specific people, deals, or properties.\n" +
        "- Suggest concrete next steps: calls, texts, emails, meeting ideas, and follow-up plans.\n" +
        "- Speak like a human, not like a bot. Be straightforward and kind.\n" +
        "- Do not use bullet points, asterisks, numbered lists, tables, emojis, or decorative symbols. " +
        "Write in short paragraphs instead.",
    };
  };

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;

    const userMsg = {
      id: crypto.randomUUID(),
      role: "user",
      content: text,
      timestamp: new Date().toISOString(),
    };

    setMessages((m) => [...m, userMsg]);
    if (!overrideText) setInput("");

    try {
      setLoading(true);

      const systemMessage = buildSystemMessage();

      const payloadMessages = [
        systemMessage,
        ...messages.map(({ role, content }) => ({ role, content })),
        { role: "user", content: text },
      ];

      const res = await apiFetch(CHAT_PATH, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: payloadMessages,
          model: "gpt-4o-mini",
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();

      let rawReply = data?.reply;
      if (rawReply && typeof rawReply === "object" && rawReply.content) {
        rawReply = rawReply.content;
      }

      const replyTextRaw =
        typeof rawReply === "string"
          ? rawReply
          : "I generated a response, but the format was unexpected.";

      const replyText = cleanReply(replyTextRaw);

      const asstMsg = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: replyText,
        timestamp: new Date().toISOString(),
      };

      setMessages((m) => [...m, asstMsg]);
    } catch (err) {
      console.error("Chat error:", err);
      const errorMsg = {
        id: crypto.randomUUID(),
        role: "assistant",
        content:
          "I couldn’t reach the AgentBlayna server or your session may have expired. Make sure you’re logged in and the backend is running on 127.0.0.1:8000, then try again.",
        timestamp: new Date().toISOString(),
      };
      setMessages((m) => [...m, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  // Actions
  const newChat = () => {
    setMessages([seedAssistant()]);
    setInput("");
  };

  const clearChat = () => {
    if (
      !window.confirm(
        "Clear the entire chat history? This cannot be undone."
      )
    )
      return;
    setMessages([seedAssistant()]);
    setInput("");
    localStorage.removeItem(STORAGE_KEY);
  };

  const exportChat = () => {
    const blob = new Blob([JSON.stringify(messages, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const dt = new Date().toISOString().replace(/[:.]/g, "-");
    const a = document.createElement("a");
    a.href = url;
    a.download = `agentblayna-crm-chat-${dt}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const SuggestChip = ({ label }) => (
    <Chip
      size="small"
      label={label}
      onClick={() => send(label)}
      sx={{
        bgcolor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.divider}`,
        fontWeight: 600,
        borderRadius: 999,
        "&:hover": {
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          borderColor: alpha(theme.palette.primary.main, 0.4),
        },
      }}
    />
  );

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Paper
        elevation={0}
        sx={{
          width: "min(980px, 92vw)",
          minHeight: "60vh",
          bgcolor: "background.paper",
          border: (t) => `1px solid ${t.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.4,
            py: 1.6,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 1,
            justifyContent: "space-between",
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
            background: `linear-gradient(120deg, ${alpha(
              theme.palette.primary.main,
              0.14
            )}, ${alpha(theme.palette.primary.main, 0.02)})`,
          }}
        >
          <Stack direction="row" spacing={1.4} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar
              src={AgentBlaynaLogo}
              alt="AgentBlayna"
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                bgcolor: "#ffffff",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" fontWeight={800} noWrap>
                AgentBlayna
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Helps you decide who to contact next and what to say.
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ flexShrink: 0 }}>
            <Chip
              size="small"
              label={loading ? "Thinking" : "Ready"}
              sx={{
                bgcolor: alpha(theme.palette.success.main, 0.12),
                color: theme.palette.success.main,
                fontWeight: 700,
                height: 24,
              }}
            />
            {crmLoading && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <CircularProgress size={12} />
                <Typography variant="caption" color="text.secondary">
                  Loading CRM data
                </Typography>
              </Stack>
            )}
            {crmError && (
              <Typography variant="caption" color="error">
                {crmError}
              </Typography>
            )}

            <Tooltip title="New chat">
              <span>
                <IconButton size="small" onClick={newChat} color="inherit">
                  <NoteAddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Clear chat">
              <span>
                <IconButton
                  size="small"
                  onClick={clearChat}
                  color="inherit"
                >
                  <DeleteSweepIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Download chat as JSON">
              <span>
                <IconButton
                  size="small"
                  onClick={exportChat}
                  color="inherit"
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        {/* Primary suggestions */}
        <Box
          sx={{
            px: 2.4,
            py: 1.2,
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
            bgcolor: theme.palette.grey[50],
          }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <SuggestChip label="Summarize my active pipeline" />
            <SuggestChip label="Who needs a follow-up today?" />
            <SuggestChip label="Help me plan my next five client touches" />
            <SuggestChip label="Review my listings and tell me what to fix" />
          </Stack>
        </Box>

        {/* Contextual next actions */}
        <Box
          sx={{
            px: 2.4,
            py: 1,
            borderBottom: (t) => `1px dashed ${t.palette.divider}`,
            bgcolor: "background.paper",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              Suggested next questions
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {nextActionSuggestions.slice(0, 4).map((s) => (
              <SuggestChip key={s} label={s} />
            ))}
          </Stack>
        </Box>

        {/* Messages */}
        <Box
          ref={scrollRef}
          sx={{
            px: 2.4,
            py: 2,
            height: { xs: "42vh", sm: "48vh", md: "50vh" },
            overflowY: "auto",
            bgcolor: "background.paper",
          }}
        >
          {messages.map((m) => {
            const isUser = m.role === "user";
            return (
              <Stack
                key={m.id}
                direction={isUser ? "row-reverse" : "row"}
                alignItems="flex-start"
                spacing={1.4}
                sx={{ mb: 1.8 }}
              >
                <Avatar
                  src={!isUser ? AgentBlaynaLogo : undefined}
                  alt={isUser ? "You" : "AgentBlayna"}
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: 13,
                    bgcolor: isUser
                      ? theme.palette.grey[300]
                      : "#ffffff",
                    color: isUser ? theme.palette.text.primary : "#000",
                    fontWeight: 700,
                    border: !isUser
                      ? `1px solid ${alpha(
                          theme.palette.primary.main,
                          0.3
                        )}`
                      : "none",
                  }}
                >
                  {isUser ? "You" : null}
                </Avatar>

                <Box
                  sx={{
                    maxWidth: { xs: "92%", sm: "82%", md: "75%" },
                    p: 1.4,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: isUser
                      ? alpha(theme.palette.primary.main, 0.06)
                      : alpha(theme.palette.background.paper, 0.96),
                    color: "text.primary",
                    boxShadow: isUser
                      ? "0 4px 12px rgba(15,23,42,0.08)"
                      : "0 3px 10px rgba(15,23,42,0.05)",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-line",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {m.content}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      mt: 0.75,
                      textAlign: isUser ? "right" : "left",
                    }}
                  >
                    {formatTime(m.timestamp)}
                  </Typography>
                </Box>
              </Stack>
            );
          })}
        </Box>

        <Divider />

        {/* Composer */}
        <Box
          sx={{
            p: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "background.paper",
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder='Ask anything, for example: "Who should I call first today?"'
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
          />
          <Button
            variant="contained"
            endIcon={<SendIcon />}
            onClick={() => send()}
            disabled={loading}
          >
            {loading ? "Sending" : "Send"}
          </Button>
          <Tooltip title="Ask again using your last question">
            <span>
              <IconButton
                onClick={() => lastUser && send(lastUser.content)}
                disabled={!lastUser || loading}
                color="inherit"
              >
                <ReplayIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Paper>
    </Box>
  );
}
